import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Plus, Trash2, UserPlus, Pencil, Mail, ArrowRightLeft } from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { PortalDialogContent as DialogContent } from '../PortalDialogContent';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';

import portalApi from '../lib/portalApi';
import { usePortalAuth } from '../PortalAuthContext';
import { PortalEmptyState } from '../ui/PortalEmptyState';

const RELATIONS = [
  { value: 'child', label: 'My child' },
  { value: 'parent', label: 'My parent' },
  { value: 'spouse', label: 'My spouse' },
  { value: 'sibling', label: 'My sibling' },
  { value: 'other', label: 'Someone else I care for' },
];

const relationLabel = (value) => RELATIONS.find(r => r.value === value)?.label || 'Family member';
const errText = (e, fallback) => e?.response?.data?.detail || fallback;

export default function PortalFamilyPage() {
  const { family, refreshFamily, activeMemberId, setActiveMember } = usePortalAuth();
  const [loading, setLoading] = useState(true);
  const [maxMembers, setMaxMembers] = useState(8);
  const [editing, setEditing] = useState(null);
  const [converting, setConverting] = useState(null);
  const [removing, setRemoving] = useState(null);
  const [convertEmail, setConvertEmail] = useState('');
  const [form, setForm] = useState({ name: '', relation: 'child', dob: '', weight_kg: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let live = true;
    portalApi.get('/family')
      .then(r => { if (live) setMaxMembers(r.data.max_members || 8); })
      .catch(() => {})
      .finally(() => { if (live) setLoading(false); });
    refreshFamily();
    return () => { live = false; };
  }, [refreshFamily]);

  const openAdd = () => { setForm({ name: '', relation: 'child', dob: '', weight_kg: '' }); setEditing('new'); };
  const openEdit = (member) => {
    setForm({ name: member.name, relation: member.relation, dob: member.dob || '', weight_kg: member.weight_kg ?? '' });
    setEditing(member.patient_user_id);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error('Enter their full name.'); return; }
    const payload = { ...form, weight_kg: form.weight_kg === '' ? null : Number(form.weight_kg) };
    setSaving(true);
    try {
      if (editing === 'new') await portalApi.post('/family', payload);
      else await portalApi.put(`/family/${editing}`, payload);
      await refreshFamily();
      setEditing(null);
      toast.success(editing === 'new' ? 'Family member added' : 'Family member updated');
    } catch (e) {
      toast.error(errText(e, 'Could not save this family member.'));
    } finally { setSaving(false); }
  };

  const remove = async (member) => {
    setSaving(true);
    try {
      await portalApi.delete(`/family/${member.patient_user_id}`);
      if (activeMemberId === member.patient_user_id) setActiveMember('');
      await refreshFamily();
      setRemoving(null);
      toast.success(`${member.name} removed from your family profiles`);
    } catch (e) {
      toast.error(errText(e, 'Could not remove this family member.'));
    } finally { setSaving(false); }
  };

  const convert = async () => {
    setSaving(true);
    try {
      const r = await portalApi.post(`/family/${converting.patient_user_id}/convert`, { email: convertEmail.trim() });
      if (activeMemberId === converting.patient_user_id) setActiveMember('');
      await refreshFamily();
      setConverting(null);
      setConvertEmail('');
      toast.success(r.data.link_sent
        ? `${r.data.member.name} can now sign in — a sign-in link is on its way.`
        : `${r.data.member.name} now has their own account.`);
    } catch (e) {
      toast.error(errText(e, 'Could not convert this profile.'));
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-5" data-testid="portal-family-page">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" data-testid="portal-family-title">Family profiles</h1>
          <p className="text-sm text-slate-500 mt-1">
            Manage prescriptions, refills and reminders for a child or an elderly parent from this same login.
          </p>
        </div>
        <Button onClick={openAdd} disabled={family.length >= maxMembers} className="bg-emerald-600 hover:bg-emerald-700 shrink-0" data-testid="portal-family-add">
          <UserPlus className="w-4 h-4 mr-1" />Add
        </Button>
      </div>

      {loading ? (
        <div className="py-10 text-center text-slate-400" data-testid="portal-family-loading"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
      ) : family.length === 0 ? (
        <PortalEmptyState
          icon={UserPlus}
          title="No family members yet"
          description="Add a child or an elderly parent, then switch to their profile to request refills or upload a prescription for them."
          action={<Button onClick={openAdd} className="bg-emerald-600 hover:bg-emerald-700" data-testid="portal-family-add-empty"><Plus className="w-4 h-4 mr-1" />Add a family member</Button>}
          testId="portal-family-empty"
        />
      ) : (
        <div className="space-y-3" data-testid="portal-family-list">
          {family.map((member) => (
            <Card key={member.patient_user_id} data-testid={`portal-family-card-${member.patient_user_id}`}>
              <CardHeader className="pb-2">
                <CardTitle className="flex flex-wrap items-center gap-2 text-base">
                  {member.name}
                  <Badge variant="secondary">{relationLabel(member.relation)}</Badge>
                  {activeMemberId === member.patient_user_id && <Badge className="bg-emerald-600">Viewing now</Badge>}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {member.dob && <p className="text-sm text-slate-500">Date of birth: {member.dob}</p>}
                <p className="text-sm text-slate-500" data-testid={`portal-family-weight-${member.patient_user_id}`}>
                  {member.weight_kg
                    ? <>Weight: {member.weight_kg} kg{member.weight_recorded_at ? ` · recorded ${new Date(member.weight_recorded_at).toLocaleDateString()}` : ''}</>
                    : <>No weight on file — add it so dose checks are sharper than age alone.</>}
                </p>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" onClick={() => setActiveMember(member.patient_user_id)}
                    disabled={activeMemberId === member.patient_user_id}
                    data-testid={`portal-family-switch-${member.patient_user_id}`}>
                    <ArrowRightLeft className="w-4 h-4 mr-1" />Switch to {member.name.split(' ')[0]}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => openEdit(member)} data-testid={`portal-family-edit-${member.patient_user_id}`}>
                    <Pencil className="w-4 h-4 mr-1" />Edit
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => { setConverting(member); setConvertEmail(''); }}
                    data-testid={`portal-family-convert-${member.patient_user_id}`}>
                    <Mail className="w-4 h-4 mr-1" />Give own login
                  </Button>
                  <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-700" disabled={saving}
                    onClick={() => setRemoving(member)} data-testid={`portal-family-remove-${member.patient_user_id}`}>
                    <Trash2 className="w-4 h-4 mr-1" />Remove
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
          <p className="text-xs text-slate-500" data-testid="portal-family-limit">You can manage up to {maxMembers} family members.</p>
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent data-testid="portal-family-dialog">
          <DialogHeader>
            <DialogTitle>{editing === 'new' ? 'Add a family member' : 'Edit family member'}</DialogTitle>
            <DialogDescription>Their prescriptions and orders stay separate from yours.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Full name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="portal-family-name" />
            </div>
            <div>
              <Label>Relationship</Label>
              <Select value={form.relation} onValueChange={(v) => setForm({ ...form, relation: v })}>
                <SelectTrigger data-testid="portal-family-relation"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {RELATIONS.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Date of birth</Label>
              <Input type="date" value={form.dob} onChange={(e) => setForm({ ...form, dob: e.target.value })} data-testid="portal-family-dob" />
            </div>
            <div>
              <Label>Current weight (kg)</Label>
              <Input type="number" min="1" max="150" step="0.1" placeholder="e.g. 18.5" value={form.weight_kg}
                onChange={(e) => setForm({ ...form, weight_kg: e.target.value })} data-testid="portal-family-weight" />
              <p className="text-xs text-slate-500 mt-1">Used to check a child’s dose more precisely than age alone. Update it as they grow.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditing(null)} data-testid="portal-family-cancel">Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="portal-family-save">
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-1" />}Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!removing} onOpenChange={(open) => !open && setRemoving(null)}>
        <DialogContent data-testid="portal-family-remove-dialog">
          <DialogHeader>
            <DialogTitle>Remove {removing?.name}?</DialogTitle>
            <DialogDescription>
              You will no longer see their prescriptions, refills or reminders. Their records stay with the
              pharmacy, so nothing is lost on their side.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRemoving(null)} data-testid="portal-family-remove-cancel">Keep</Button>
            <Button variant="destructive" disabled={saving} onClick={() => remove(removing)}
              data-testid="portal-family-remove-confirm">
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-1" />}Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!converting} onOpenChange={(open) => !open && setConverting(null)}>
        <DialogContent data-testid="portal-family-convert-dialog">
          <DialogHeader>
            <DialogTitle>Give {converting?.name} their own login</DialogTitle>
            <DialogDescription>
              They get their own sign-in link by email. You will no longer be able to view or manage their
              prescriptions unless they share access with you again.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label>Their email address</Label>
            <Input type="email" value={convertEmail} onChange={(e) => setConvertEmail(e.target.value)}
              placeholder="name@example.com" data-testid="portal-family-convert-email" />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConverting(null)} data-testid="portal-family-convert-cancel">Cancel</Button>
            <Button onClick={convert} disabled={saving || !convertEmail.trim()} className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="portal-family-convert-confirm">
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-1" />}Send sign-in link
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
